import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { StickableTanstackTable } from 'components/common/Table/StickableTanstackTable';
import {
  DeviceUnitLevel,
  DeviceUnitRecord
} from 'core/deviceManagement/DeviceManagement';
import {
  buildDeviceListColumns,
  DEVICE_UNIT_KEY_FIELD,
  resolveDeviceUnitLevel
} from './deviceListColumns';
import { DeviceDetailPanel } from './DeviceDetailPanel';
import { DeviceExportModal } from './DeviceExportModal';
import { DeviceListModel } from './DeviceListModel';
import { DeviceListToolbar } from './DeviceListToolbar';
import styles from './deviceManagement.module.scss';

type DeviceListProps = {
  salesChannel?: number;
  channelDeliveryUnit?: DeviceUnitLevel;
  model: DeviceListModel;
  onOpenImport: () => void;
  /** 名單模式的檔名，匯出面板要把它寫進條件摘要。 */
  listFileName?: string;
};

/**
 * 機台清單檢視。
 *
 * 分頁、排序、搜尋全部在後端做（表格傳 `remote`）：清單會到上萬列，
 * 前端分頁得先把全部資料抓回來才排得了序。
 *
 * 查詢狀態由頁面那一層持有（見 DeviceManagement），所以這個元件在換分頁時被卸載
 * 也不會掉資料，切回來條件沒變就不會重查。
 */
export const DeviceList: React.FC<DeviceListProps> = ({
  salesChannel,
  channelDeliveryUnit,
  model,
  onOpenImport,
  listFileName
}) => {
  const {
    loading,
    records,
    totalCount,
    failed,
    request,
    pagination,
    defaultSorted,
    searchString,
    onTableChange,
    onHandleSearch,
    exporting,
    exportUnits
  } = model;
  const noChannel = salesChannel === undefined;
  const [detailUnit, setDetailUnit] = useState<DeviceUnitRecord>();
  const [showExport, setShowExport] = useState(false);

  const onShowDetail = useCallback((record: DeviceUnitRecord) => {
    setDetailUnit(record);
  }, []);

  // 換渠道後開著的那一筆屬於上一個渠道，留著會用新渠道去查一個不存在的 unit。
  useEffect(() => {
    setDetailUnit(undefined);
  }, [salesChannel]);

  const unitLevel = useMemo(
    () => resolveDeviceUnitLevel(records, channelDeliveryUnit),
    [records, channelDeliveryUnit]
  );

  const columns = useMemo(
    () => buildDeviceListColumns(unitLevel, onShowDetail),
    [unitLevel, onShowDetail]
  );

  const onOpenExport = useCallback(() => setShowExport(true), []);

  return (
    <div className={styles.tabContent}>
      {loading && <LoadingIndicator />}
      {/* 操作列不交給表格自己畫：表格畫的那一版把搜尋列與按鈕排成同一列的並列子項，
          排不出設計稿要的「搜尋列自己一行、匯出靠右」。 */}
      <DeviceListToolbar
        defaultSearchString={searchString}
        onSearch={onHandleSearch}
        onOpenImport={onOpenImport}
        onOpenExport={onOpenExport}
        disabled={noChannel}
      />
      <StickableTanstackTable<DeviceUnitRecord>
        customPadding={false}
        remote
        keyField={DEVICE_UNIT_KEY_FIELD}
        data={records}
        columns={columns}
        // 零列有三種原因，而使用者該做的事各不相同：還沒選渠道（先選渠道）、
        // 查過了但沒有符合的（改篩選）、查壞了（重試）。都寫成「找不到符合條件的資料」
        // 會讓後兩種被誤讀成資料真的是空的。
        noDataIndication={i18n.t(
          noChannel
            ? 'deviceManagement.messages.selectChannelFirst'
            : failed
              ? 'deviceManagement.messages.listLoadFailed'
              : 'deviceManagement.messages.noUnit'
        )}
        defaultSorted={defaultSorted}
        onTableChange={onTableChange}
        pagination={pagination}
      />
      {detailUnit && salesChannel !== undefined && (
        <DeviceDetailPanel
          channelId={salesChannel}
          record={detailUnit}
          onClose={() => setDetailUnit(undefined)}
        />
      )}
      {showExport && (
        <DeviceExportModal
          totalCount={totalCount}
          sortField={request.sortField}
          sortOrder={request.sortOrder}
          filter={request.filter}
          search={request.search}
          listFileName={listFileName}
          exporting={exporting}
          onExport={exportUnits}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  );
};
